import { useMemo } from 'react';

import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';
import type { AccessBackfillResult, AccessGroup, SubjectType } from '@/shared/access';

import {
  backfillAccess,
  createAccessGroup,
  deleteAccessGroup,
  getAccessCatalogue,
  getAccessGroup,
  getAccessHistory,
  getAccessReview,
  getEffectiveAccess,
  getMyEffectiveAccess,
  getSubjectGrants,
  listAccessGroups,
  moveAccessGroup,
  setAccessGroupMembers,
  setSubjectGrants,
  setUserGroupMemberships,
  type AccessHistoryQuery,
  type AccessReviewQuery,
  type CreateAccessGroupBody,
  type SetSubjectGrantsBody,
  type UserGroupMembershipChange,
} from './api/accessApi';

export const ACCESS_QUERY_KEY = ['access'] as const;
export const accessCatalogueKey = [...ACCESS_QUERY_KEY, 'catalogue'] as const;
export const accessGroupsKey = [...ACCESS_QUERY_KEY, 'groups'] as const;
export const accessGroupKey = (groupId: string) => [...accessGroupsKey, groupId] as const;
export const effectiveAccessKey = (userId: string) =>
  [...ACCESS_QUERY_KEY, 'effective', userId] as const;
export const subjectGrantsKey = (subjectType: SubjectType, subjectId: string) =>
  [...ACCESS_QUERY_KEY, 'grants', subjectType, subjectId] as const;

/**
 * The platform catalogue. Cached hard on purpose: it is code-owned and only changes on a platform
 * release, so re-fetching it while an administrator edits a matrix is pure noise.
 */
export function useAccessCatalogue(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accessCatalogueKey,
    queryFn: getAccessCatalogue,
    staleTime: 60 * 60_000,
    enabled: options?.enabled ?? true,
  });
}

export function useAccessGroups(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accessGroupsKey,
    queryFn: listAccessGroups,
    enabled: options?.enabled ?? true,
  });
}

/** One group including its membership — the list endpoint deliberately omits `memberIds`. */
export function useAccessGroup(groupId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accessGroupKey(groupId ?? ''),
    queryFn: () => getAccessGroup(groupId as string),
    enabled: Boolean(groupId) && (options?.enabled ?? true),
  });
}

export function useEffectiveAccess(userId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: effectiveAccessKey(userId ?? ''),
    queryFn: () => getEffectiveAccess(userId as string),
    enabled: Boolean(userId) && (options?.enabled ?? true),
  });
}

/** Every staff user may ask about themselves — no administrative permission required. */
export function useMyEffectiveAccess(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...ACCESS_QUERY_KEY, 'effective', 'me'],
    queryFn: getMyEffectiveAccess,
    enabled: options?.enabled ?? true,
  });
}

export function useSubjectGrants(
  subjectType: SubjectType,
  subjectId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: subjectGrantsKey(subjectType, subjectId ?? ''),
    queryFn: () => getSubjectGrants(subjectType, subjectId as string),
    enabled: Boolean(subjectId) && (options?.enabled ?? true),
  });
}

/**
 * What every department grants, for the attestation half of the access review (plan-auth-001 Phase 4.5).
 *
 * <p>One query per department rather than one call for all of them, because the platform exposes no bulk
 * grant read — and because sharing {@link subjectGrantsKey} with the matrix editor is worth more than
 * saving requests: a department the reviewer has just edited is already in the cache, and a revoke made
 * here invalidates the editor's copy for free. Departments are counted in tens, not thousands.
 */
export function useGroupGrants(groupIds: string[], options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  return useQueries({
    queries: groupIds.map((groupId) => ({
      queryKey: subjectGrantsKey('GROUP', groupId),
      queryFn: () => getSubjectGrants('GROUP', groupId),
      enabled,
    })),
  });
}

/**
 * Every department, with its roster — the only way to answer "which departments is this person in?".
 *
 * <p>There is no per-user endpoint: Keycloak owns membership and the BFF exposes it only group-first
 * ({@code GET /access/groups/{id}}, the one call that returns `memberIds`). So the question is answered
 * by reading every department once and inverting the result. Costly-sounding, cheap in practice for the
 * same reason {@link useGroupGrants} accepts it: departments are counted in tens, the reads are cached
 * per group and shared by every row on the page, and a membership edit invalidates them anyway.
 *
 * <p>Returns `byUser` rather than making each caller invert it, so the staff table and the drawer agree
 * about who is where by construction.
 */
export function useDepartmentMemberships(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const groups = useAccessGroups({ enabled });
  const ids = useMemo(() => (groups.data ?? []).map((group) => group.id), [groups.data]);

  const details = useQueries({
    queries: ids.map((groupId) => ({
      queryKey: accessGroupKey(groupId),
      queryFn: () => getAccessGroup(groupId),
      enabled,
    })),
  });

  return useMemo(() => {
    const loaded = details.flatMap((query) => (query.data ? [query.data] : []));
    const byUser = new Map<string, AccessGroup[]>();
    for (const group of loaded) {
      for (const userId of group.memberIds ?? []) {
        const existing = byUser.get(userId);
        if (existing) existing.push(group);
        else byUser.set(userId, [group]);
      }
    }
    return {
      /** Every department, in the list endpoint's order — the tree order the group screens use. */
      groups: groups.data ?? [],
      /** Departments each user is a **direct** member of. Ancestors are implied, not listed here. */
      byUser,
      isLoading: groups.isLoading || details.some((query) => query.isLoading),
      isError: groups.isError || details.some((query) => query.isError),
      error: groups.error ?? details.find((query) => query.error)?.error ?? null,
      refetch: () => {
        void groups.refetch();
        details.forEach((query) => void query.refetch());
      },
    };
  }, [groups, details]);
}

/** Moves one person between departments — see `setUserGroupMemberships` for why it is not one call. */
export function useSetUserGroups() {
  const queryClient = useQueryClient();
  return useMutation<void, NormalizedApiError, UserGroupMembershipChange>({
    mutationFn: setUserGroupMemberships,
    onSuccess: () => {
      // Same reasoning as useSetAccessGroupMembers: membership changes what someone can do, so every
      // cached resolution is suspect, not just the groups that were touched.
      void queryClient.invalidateQueries({ queryKey: ACCESS_QUERY_KEY });
    },
  });
}

/**
 * The tenant-wide review.
 *
 * <p>Each page costs an employee lookup plus a bulk resolution, so it is not kept fresh in the background:
 * a review is a deliberate sit-down, and a matrix reshuffling under the reviewer would be worse than a
 * slightly stale one.
 *
 * <p>`enabled` exists so a reviewer who lacks the permission issues no request at all: the BFF would
 * refuse it anyway, and firing a request the caller is known to be denied only costs a 403 in the log and
 * an error state the screen has already decided not to show.
 */
export function useAccessReview(query: AccessReviewQuery = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...ACCESS_QUERY_KEY, 'review', query],
    queryFn: () => getAccessReview(query),
    staleTime: 60_000,
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  });
}

export function useAccessHistory(query: AccessHistoryQuery = {}) {
  return useQuery({
    queryKey: [...ACCESS_QUERY_KEY, 'history', query],
    queryFn: () => getAccessHistory(query),
    placeholderData: (previous) => previous,
  });
}

export function useBackfillAccess() {
  const queryClient = useQueryClient();
  return useMutation<AccessBackfillResult, NormalizedApiError, void>({
    mutationFn: backfillAccess,
    onSuccess: () => {
      // The migration creates groups, grants AND memberships in one go, so nothing cached about access
      // survives it.
      void queryClient.invalidateQueries({ queryKey: ACCESS_QUERY_KEY });
    },
  });
}

export function useCreateAccessGroup() {
  const queryClient = useQueryClient();
  return useMutation<AccessGroup, NormalizedApiError, CreateAccessGroupBody>({
    mutationFn: createAccessGroup,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accessGroupsKey });
    },
  });
}

export function useDeleteAccessGroup() {
  const queryClient = useQueryClient();
  return useMutation<void, NormalizedApiError, string>({
    mutationFn: deleteAccessGroup,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accessGroupsKey });
    },
  });
}

export function useSetAccessGroupMembers() {
  const queryClient = useQueryClient();
  return useMutation<AccessGroup, NormalizedApiError, { groupId: string; userIds: string[] }>({
    mutationFn: ({ groupId, userIds }) => setAccessGroupMembers(groupId, userIds),
    onSuccess: () => {
      // Membership changes what people can do, so every cached resolution is suspect — not just the
      // group's own row.
      void queryClient.invalidateQueries({ queryKey: ACCESS_QUERY_KEY });
    },
  });
}

export function useMoveAccessGroup() {
  const queryClient = useQueryClient();
  return useMutation<AccessGroup, NormalizedApiError, { groupId: string; parentId?: string }>({
    mutationFn: ({ groupId, parentId }) => moveAccessGroup(groupId, parentId),
    onSuccess: () => {
      // A move re-parents the group's whole subtree, so everyone in it now inherits a different set of
      // ancestors — the same "every cached resolution is suspect" reasoning as
      // useSetAccessGroupMembers, just triggered by the tree instead of the roster.
      void queryClient.invalidateQueries({ queryKey: ACCESS_QUERY_KEY });
    },
  });
}

export function useSetSubjectGrants() {
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    NormalizedApiError,
    { subjectType: SubjectType; subjectId: string; body: SetSubjectGrantsBody }
  >({
    mutationFn: ({ subjectType, subjectId, body }) =>
      setSubjectGrants(subjectType, subjectId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ACCESS_QUERY_KEY });
    },
  });
}
